// Sơ đồ cây mạng lưới — cây THỤT LỀ (docs/KE_HOACH_NPP_SANG_WEB.md §10): dựng cây từ danh sách đơn vị phẳng
// (parId = cấp trên, null = trực thuộc NPP) rồi trải thành các hàng đang hiện, kèm đường kẻ nối cho từng cột
// thụt lề. Dùng chung cho web (/npp/network) và trang quản trị (Super Admin, /platform/network).
//
// Không đơn vị nào được rơi khỏi cây: cấp trên không có trong danh sách (đã xoá / dữ liệu lệch) hay vòng lặp
// parId → đơn vị đó hiện ở gốc, đánh dấu `orphan`.

export type NetworkUnitType = 'agency' | 'ctv' | 'shop'

export interface NetworkUnitBase {
  id: string
  name: string
  tenantType: NetworkUnitType
  parId: string | null
  /** Mã trong NPP — DL01, CTV01, S01 (networkUnitCodes). */
  code?: string
}

export type NetworkCounts = Record<NetworkUnitType, number>

/** Một đơn vị của API cây (apps/admin-api/src/lib/networkTree.ts) sau khi qua JSON. */
export interface NetworkTreeUnitDto extends NetworkUnitBase {
  status: string
  /** Đang khoá (tiền tạm giữ, cấp dưới vẫn chạy). */
  locked: boolean
  createdAt: string
  owner: { name: string | null; phone: string | null } | null
  priceList: { id: string; code: string; name: string } | null
  /** CTV: bảng chiết khấu của cấp trên hiện tại (null = chưa có); đơn vị khác không có ô */
  scheme?: { code: string; name: string } | null
  orders30d: number
}

export interface NetworkNode<U extends NetworkUnitBase> {
  unit: U
  parent: NetworkNode<U> | null
  children: NetworkNode<U>[]
  /** 0 = trực thuộc NPP. */
  depth: number
  /** Số đơn vị trong nhánh (không tính chính nó), theo loại. */
  below: NetworkCounts
  /** Cấp trên không tìm thấy — đưa lên gốc để không mất. */
  orphan: boolean
}

export interface NetworkTree<U extends NetworkUnitBase> {
  roots: NetworkNode<U>[]
  byId: Map<string, NetworkNode<U>>
  counts: NetworkCounts
}

/** Cột thụt lề phía trước một hàng: `pipe` = nhánh trên còn em phía dưới, `tee` / `elbow` = cột nối vào chính hàng. */
export type NetworkGuide = 'pipe' | 'blank' | 'tee' | 'elbow'

export interface NetworkRow<U extends NetworkUnitBase> {
  node: NetworkNode<U>
  guides: NetworkGuide[]
  /** Đang mở (có cấp dưới và đang hiện cấp dưới). */
  open: boolean
  /** Bấm mở / gập được — khi đang lọc thì không: cây tự mở đúng các nhánh có kết quả. */
  toggleable: boolean
  /** Khớp từ khoá tìm — khi tìm, hàng cha hiện để giữ ngữ cảnh nhưng không khớp. */
  hit: boolean
}

const TYPE_ORDER: Record<NetworkUnitType, number> = { agency: 0, ctv: 1, shop: 2 }

export const NETWORK_TYPE_LABEL: Record<NetworkUnitType, string> = { agency: 'Đại lý', ctv: 'CTV', shop: 'Shop' }

const STATUS_LABEL: Record<string, string> = { active: 'Hoạt động', suspended: 'Tạm ngưng', migrating: 'Đang chuyển dữ liệu', grace: 'Ân hạn' }
export const networkStatusLabel = (status: string): string => STATUS_LABEL[status] ?? status

export interface NetworkIssue {
  label: string
  severity: 'danger' | 'warn'
}

/** Việc cần xử lý của một đơn vị — nhãn trên hàng cây và bộ lọc "Cần xử lý". */
export function networkUnitIssues(u: Pick<NetworkTreeUnitDto, 'status' | 'locked' | 'priceList' | 'scheme'>): NetworkIssue[] {
  const out: NetworkIssue[] = []
  if (u.status !== 'active') out.push({ label: networkStatusLabel(u.status), severity: u.status === 'suspended' ? 'danger' : 'warn' })
  if (u.locked) out.push({ label: 'Đang khóa', severity: 'danger' })
  if (!u.priceList) out.push({ label: 'Chưa có bảng giá', severity: 'warn' })
  if (u.scheme === null) out.push({ label: 'Chưa có bảng chiết khấu', severity: 'warn' })
  return out
}

/** "1 Đại lý · 4 CTV · 10 Shop" — bỏ loại bằng 0. */
export function networkCountsText(c: NetworkCounts, empty = 'Chưa có cấp dưới'): string {
  const parts = (['agency', 'ctv', 'shop'] as const).filter((t) => c[t] > 0).map((t) => `${c[t].toLocaleString('vi-VN')} ${NETWORK_TYPE_LABEL[t]}`)
  return parts.length ? parts.join(' · ') : empty
}

/** "Đại lý Minh Phát" — thêm tên loại phía trước, trừ khi tên đã có sẵn ("Đại lý B1", "CTV Hoa Lan"). */
export function networkUnitTitle(u: Pick<NetworkUnitBase, 'name' | 'tenantType'>): string {
  const label = NETWORK_TYPE_LABEL[u.tenantType]
  return fold(u.name).startsWith(fold(label)) ? u.name : `${label} ${u.name}`
}

export const emptyCounts = (): NetworkCounts => ({ agency: 0, ctv: 0, shop: 0 })

/** Chữ đầu mã đơn vị: DL01, CTV01, S01 (S như ô loại Shop trên sơ đồ). */
export const NETWORK_CODE_PREFIX: Record<NetworkUnitType, string> = { agency: 'DL', ctv: 'CTV', shop: 'S' }

/**
 * Mã đơn vị trong một NPP: mỗi loại đánh số riêng theo thứ tự trở thành loại đó (`since` = ngày tạo, hoặc ngày
 * đổi loại gần nhất) — đơn vị mới / vừa lên Đại lý nhận số kế tiếp, số cũ không dịch. Truyền CẢ đơn vị đã xoá
 * để xoá một đơn vị không làm các đơn vị sau đổi số. Ít nhất 2 chữ số: DL01 … DL99, DL100.
 */
export function networkUnitCodes(units: ReadonlyArray<{ id: string; tenantType: string; since: Date }>): Map<string, string> {
  const out = new Map<string, string>()
  for (const type of Object.keys(NETWORK_CODE_PREFIX) as NetworkUnitType[]) {
    const ofType = units
      .filter((u) => (u.tenantType === 'agency' || u.tenantType === 'ctv' ? u.tenantType : 'shop') === type)
      .sort((a, b) => a.since.getTime() - b.since.getTime() || a.id.localeCompare(b.id))
    ofType.forEach((u, i) => out.set(u.id, `${NETWORK_CODE_PREFIX[type]}${String(i + 1).padStart(2, '0')}`))
  }
  return out
}

/** Số trong mã (DL07 → 7); không có mã → vô cùng (xếp sau). */
const codeNo = (u: NetworkUnitBase) => (u.code ? Number(u.code.replace(/\D/g, '')) : Number.POSITIVE_INFINITY)

/** So hai đơn vị cùng loại theo mã (DL01 trước DL02); 0 khi một bên chưa có mã — người gọi xếp tiếp theo cách khác. */
export function networkCodeOrder(a: NetworkUnitBase, b: NetworkUnitBase): number {
  return (a.code && b.code && codeNo(a) - codeNo(b)) || 0
}

// Cùng loại: theo mã (DL01, DL02…) để sơ đồ đọc lần lượt; chưa có mã thì theo tên.
const byTypeThenName = (a: NetworkNode<NetworkUnitBase>, b: NetworkNode<NetworkUnitBase>) =>
  TYPE_ORDER[a.unit.tenantType] - TYPE_ORDER[b.unit.tenantType] ||
  codeNo(a.unit) - codeNo(b.unit) ||
  a.unit.name.localeCompare(b.unit.name, 'vi')

export function buildNetworkTree<U extends NetworkUnitBase>(units: readonly U[]): NetworkTree<U> {
  const byId = new Map<string, NetworkNode<U>>()
  for (const unit of units) byId.set(unit.id, { unit, parent: null, children: [], depth: 0, below: emptyCounts(), orphan: false })

  const roots: NetworkNode<U>[] = []
  for (const node of byId.values()) {
    const parId = node.unit.parId
    const parent = parId ? byId.get(parId) : undefined
    if (parent && parent !== node) {
      node.parent = parent
      parent.children.push(node)
    } else {
      node.orphan = !!parId
      roots.push(node)
    }
  }

  // Duyệt từ gốc: đặt depth, cộng nhánh. Nút không với tới được từ gốc là nằm trong vòng lặp parId — cắt vòng
  // ở nút đó, đưa lên gốc.
  const seen = new Set<NetworkNode<U>>()
  const walk = (node: NetworkNode<U>, depth: number) => {
    seen.add(node)
    node.depth = depth
    node.below = emptyCounts()
    node.children.sort(byTypeThenName)
    for (const child of node.children) {
      walk(child, depth + 1)
      node.below[child.unit.tenantType] += 1
      for (const t of Object.keys(TYPE_ORDER) as NetworkUnitType[]) node.below[t] += child.below[t]
    }
  }
  roots.sort(byTypeThenName)
  for (const root of roots) walk(root, 0)
  for (const node of byId.values()) {
    if (seen.has(node)) continue
    const parent = node.parent!
    parent.children = parent.children.filter((c) => c !== node)
    node.parent = null
    node.orphan = true
    roots.push(node)
    walk(node, 0)
  }

  const counts = emptyCounts()
  for (const node of byId.values()) counts[node.unit.tenantType] += 1
  return { roots, byId, counts }
}

/** Chuỗi cấp trên từ gốc tới cấp trên trực tiếp (không gồm chính nó). */
export function networkAncestors<U extends NetworkUnitBase>(node: NetworkNode<U>): NetworkNode<U>[] {
  const out: NetworkNode<U>[] = []
  for (let p = node.parent; p; p = p.parent) out.unshift(p)
  return out
}

/** Mở sẵn các cấp trên tới `depth` (0 = chỉ hiện đơn vị trực thuộc NPP). */
export function networkOpenToDepth<U extends NetworkUnitBase>(tree: NetworkTree<U>, depth: number): Set<string> {
  const open = new Set<string>()
  for (const node of tree.byId.values()) if (node.depth < depth && node.children.length) open.add(node.unit.id)
  return open
}

/**
 * Trải cây thành các hàng đang hiện.
 *  - Không tìm: cấp dưới hiện khi nút nằm trong `open`.
 *  - Có `match`: chỉ hiện nút khớp + chuỗi cấp trên của nó (tự mở) — cấp dưới không khớp của nút khớp cũng ẩn,
 *    để lọc "cần xử lý" không lẫn đơn vị bình thường. Xoá bộ lọc là về lại cây đang mở như trước.
 * `prefix`: cột thụt lề có sẵn phía trước (cây của Super Admin còn một cấp NPP bên ngoài).
 */
export function networkRows<U extends NetworkUnitBase>(
  roots: readonly NetworkNode<U>[],
  open: ReadonlySet<string>,
  match?: (unit: U) => boolean,
  prefix: readonly NetworkGuide[] = [],
): NetworkRow<U>[] {
  const keep = new Map<NetworkNode<U>, boolean>()
  const has = (node: NetworkNode<U>): boolean => {
    let v = keep.get(node)
    if (v === undefined) {
      v = match!(node.unit)
      for (const c of node.children) if (has(c)) v = true
      keep.set(node, v)
    }
    return v
  }

  const rows: NetworkRow<U>[] = []
  const filtering = !!match
  const emit = (list: readonly NetworkNode<U>[], lead: readonly NetworkGuide[]) => {
    const shown = filtering ? list.filter(has) : list
    shown.forEach((node, i) => {
      const last = i === shown.length - 1
      const isOpen = filtering ? node.children.some(has) : node.children.length > 0 && open.has(node.unit.id)
      rows.push({ node, guides: [...lead, last ? 'elbow' : 'tee'], open: isOpen, hit: filtering && match(node.unit), toggleable: !filtering })
      if (isOpen) emit(node.children, [...lead, last ? 'blank' : 'pipe'])
    })
  }
  emit(roots, prefix)
  return rows
}

/** Ô tìm: khớp tên / mã đơn vị (DL01), tên, SĐT chủ — bỏ dấu, bỏ khoảng trắng trong số điện thoại. */
export function networkMatcher<U extends NetworkUnitBase>(
  query: string,
  extra: (unit: U) => Array<string | null | undefined> = () => [],
): ((unit: U) => boolean) | undefined {
  const q = fold(query.trim())
  if (!q) return undefined
  // Gõ số điện thoại ("0987 654", "+84987…") thì so phần số, bỏ khoảng trắng / dấu chấm.
  const digits = /^[\d\s.+-]+$/.test(q) ? q.replace(/\D/g, '').replace(/^84/, '0') : ''
  return (unit) => {
    const texts = [unit.name, unit.code, ...extra(unit)]
    for (const t of texts) {
      if (!t) continue
      const f = fold(t)
      if (f.includes(q)) return true
      if (digits.length >= 3 && f.replace(/\D/g, '').replace(/^84/, '0').includes(digits)) return true
    }
    return false
  }
}

// ─── Cây ngang (máy tính) ────────────────────────────────────────────────────────────────────────────────────
// Mỗi cấp một cột trái → phải, đơn vị cùng cấp xếp dọc trong cột, ô cấp trên nằm giữa các ô cấp dưới đang hiện.
// Bề ngang chỉ theo số cấp (tối đa 4–5 cột), không theo số đơn vị — lý do cây ngang kiểu org-chart bị bỏ ngày
// 21/10 không còn. Cột 0 là các ô gốc ("nhóm"): NPP, hoặc chính Đại lý / CTV đang xem; Super Admin mỗi NPP một ô.
// Mở / gập / lọc cùng nghĩa với networkRows (điện thoại vẫn dùng cây thụt lề).

export interface NetworkLayoutGroup<U extends NetworkUnitBase> {
  /** Khoá ô gốc — không trùng mã đơn vị. */
  key: string
  roots: readonly NetworkNode<U>[]
  /** Gập cả nhóm (Super Admin: NPP chưa mở). */
  collapsed?: boolean
  /** Nhãn "+N" khi gập; không đặt thì đếm từ `roots`. */
  hiddenCount?: number
  /** Có nút mở / gập ở ô gốc. Ô NPP / chính mình thì không — luôn mở. */
  toggleable?: boolean
}

export interface NetworkLayoutOptions {
  cardW: number
  cardH: number
  /** Ô gốc cao hơn ô đơn vị. */
  rootH: number
  gapX: number
  gapY: number
  pad: number
  /** Đang lọc thì bỏ nhóm không còn đơn vị nào khớp (Super Admin nhiều NPP). */
  hideEmptyGroups: boolean
}

// Cỡ gọn (30/10): ô 184×44, gốc cao 56, khe 44 / 8 — một màn chứa nhiều hơn ~38% hàng so với bản đầu (208×60, khe 64 / 12)
export const NETWORK_LAYOUT_DEFAULTS: NetworkLayoutOptions = { cardW: 184, cardH: 44, rootH: 56, gapX: 44, gapY: 8, pad: 20, hideEmptyGroups: false }

export interface NetworkLayoutCard<U extends NetworkUnitBase> {
  /** Mã đơn vị, hoặc khoá nhóm với ô gốc. */
  key: string
  group: string
  /** null = ô gốc của nhóm. */
  node: NetworkNode<U> | null
  col: number
  x: number
  y: number
  w: number
  h: number
  /** Tâm dọc — điểm đường nối chạm vào ô. */
  cy: number
  /** Khớp bộ lọc — khi lọc, ô cấp trên hiện để giữ ngữ cảnh nhưng không khớp. */
  hit: boolean
}

/** Đường nối từ mép phải ô `from` tới mép trái ô `to`. */
export interface NetworkLayoutEdge {
  from: string
  to: string
  x1: number
  y1: number
  x2: number
  y2: number
}

/** Nút mở / gập nằm giữa mép phải ô — `hidden` là số đơn vị bên dưới (nhãn "+N" khi gập). */
export interface NetworkLayoutToggle<U extends NetworkUnitBase> {
  key: string
  group: string
  node: NetworkNode<U> | null
  x: number
  cy: number
  open: boolean
  hidden: number
}

export interface NetworkLayout<U extends NetworkUnitBase> {
  cards: NetworkLayoutCard<U>[]
  edges: NetworkLayoutEdge[]
  toggles: NetworkLayoutToggle<U>[]
  width: number
  height: number
}

const belowTotal = (n: NetworkNode<NetworkUnitBase>) => n.below.agency + n.below.ctv + n.below.shop

export function networkLayout<U extends NetworkUnitBase>(
  groups: readonly NetworkLayoutGroup<U>[],
  open: ReadonlySet<string>,
  match?: (unit: U) => boolean,
  options: Partial<NetworkLayoutOptions> = {},
): NetworkLayout<U> {
  const o = { ...NETWORK_LAYOUT_DEFAULTS, ...options }
  const filtering = !!match
  const keep = new Map<NetworkNode<U>, boolean>()
  const has = (node: NetworkNode<U>): boolean => {
    let v = keep.get(node)
    if (v === undefined) {
      v = match!(node.unit)
      for (const c of node.children) if (has(c)) v = true
      keep.set(node, v)
    }
    return v
  }
  const kidsOf = (n: NetworkNode<U>) =>
    filtering ? n.children.filter(has) : n.children.length > 0 && open.has(n.unit.id) ? n.children : []

  const cards: NetworkLayoutCard<U>[] = []
  const edges: NetworkLayoutEdge[] = []
  const toggles: NetworkLayoutToggle<U>[] = []
  const colX = (col: number) => o.pad + col * (o.cardW + o.gapX)
  let cursor = o.pad
  let maxCol = 0

  // Đặt các ô con trước (lá chiếm một hàng), ô cha lấy tâm giữa ô con đầu và cuối.
  const placeKids = (kids: readonly NetworkNode<U>[], col: number, group: string, h: number) => {
    if (!kids.length) {
      const cy = cursor + h / 2
      cursor += h + o.gapY
      return { cy, placed: [] as NetworkLayoutCard<U>[] }
    }
    const placed = kids.map((k) => placeUnit(k, col + 1, group))
    return { cy: (placed[0]!.cy + placed[placed.length - 1]!.cy) / 2, placed }
  }
  const link = (from: string, x: number, cy: number, kids: NetworkLayoutCard<U>[]) => {
    for (const k of kids) edges.push({ from, to: k.key, x1: x + o.cardW, y1: cy, x2: k.x, y2: k.cy })
  }
  const placeUnit = (n: NetworkNode<U>, col: number, group: string): NetworkLayoutCard<U> => {
    const { cy, placed } = placeKids(kidsOf(n), col, group, o.cardH)
    const x = colX(col)
    const card: NetworkLayoutCard<U> = { key: n.unit.id, group, node: n, col, x, y: Math.round(cy - o.cardH / 2), w: o.cardW, h: o.cardH, cy, hit: filtering && match!(n.unit) }
    cards.push(card)
    link(card.key, x, cy, placed)
    if (n.children.length && !filtering) toggles.push({ key: card.key, group, node: n, x: x + o.cardW, cy, open: open.has(n.unit.id), hidden: belowTotal(n) })
    maxCol = Math.max(maxCol, col)
    return card
  }

  for (const g of groups) {
    const roots = g.collapsed ? [] : filtering ? g.roots.filter(has) : g.roots
    if (filtering && o.hideEmptyGroups && !roots.length) continue
    // Ô gốc cao hơn một hàng: chừa nửa phần dư phía trên, sau đó không để nhóm sau chồng lên.
    cursor += (o.rootH - o.cardH) / 2
    const { cy, placed } = placeKids(roots, 0, g.key, o.cardH)
    const y = Math.round(cy - o.rootH / 2)
    cursor = Math.max(cursor, y + o.rootH + o.gapY)
    cards.push({ key: g.key, group: g.key, node: null, col: 0, x: colX(0), y, w: o.cardW, h: o.rootH, cy, hit: false })
    link(g.key, colX(0), cy, placed)
    if (g.toggleable && !filtering) {
      const hidden = g.hiddenCount ?? g.roots.reduce((s, r) => s + 1 + belowTotal(r), 0)
      toggles.push({ key: g.key, group: g.key, node: null, x: colX(0) + o.cardW, cy, open: !g.collapsed, hidden })
    }
  }

  return {
    cards,
    edges,
    toggles,
    width: colX(maxCol) + o.cardW + o.pad,
    height: Math.max(cursor - o.gapY + o.pad, o.pad * 2),
  }
}

/**
 * Thứ tự đọc của cây ngang (phím Tab, trình đọc màn hình): cấp trên trước cấp dưới, trên xuống dưới. networkLayout
 * đặt cấp dưới trước để tính tâm cấp trên, nên mảng `cards` đi từ lá lên gốc.
 */
export function networkLayoutOrder<U extends NetworkUnitBase>(layout: NetworkLayout<U>): NetworkLayoutCard<U>[] {
  const byKey = new Map(layout.cards.map((c) => [c.key, c]))
  const kids = new Map<string, NetworkLayoutCard<U>[]>()
  const isChild = new Set<string>()
  for (const e of layout.edges) {
    const to = byKey.get(e.to)
    if (!to) continue
    const list = kids.get(e.from) ?? []
    list.push(to)
    kids.set(e.from, list)
    isChild.add(e.to)
  }
  const byY = (a: NetworkLayoutCard<U>, b: NetworkLayoutCard<U>) => a.y - b.y
  const out: NetworkLayoutCard<U>[] = []
  const visit = (c: NetworkLayoutCard<U>) => {
    out.push(c)
    for (const k of (kids.get(c.key) ?? []).sort(byY)) visit(k)
  }
  for (const c of layout.cards.filter((x) => !isChild.has(x.key)).sort(byY)) visit(c)
  return out
}

const fold = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase()
